// <pfs-notification-bell>: Bell-Icon-Trigger mit Unread-Badge.
//
// Skeleton-Stub heute (PFS-133). Spaeter:
//   - data-source-url polled fuer Unread-Count (`{count: number}`)
//   - data-stream-url subscribed SSE fuer live-Update
//   - Click oeffnet Inbox-Dropdown (z.B. `<pfs-popover>`-Wrapper)
//
// Heute:
//   - Renderet Bell-Icon
//   - Optional Badge-Count via data-initial-count-Attribut
//   - Click dispatched `pfs:popover-toggle`-Event mit detail.targetId
//     fuer einen `<pfs-popover>` mit gleicher id (Pattern: Layout-
//     Provider rendered <pfs-popover id="bell-popover"> als Sibling)
//
// Realtime (PFS-492, badge-Phase): mit data-events-url + data-user-id
// subscribed die Bell via liveBus (PFS-397, generischer SSE-Push) das
// data-event-name-Event (default "pfs-notifications.delivery.pushed") und zaehlt das
// Badge live hoch, wenn die external_user_id im Payload der eigenen
// user-id entspricht (Filter IM WC — liveBus bleibt generisch). Pro Event
// dispatched sie zusaetzlich ein generisches `pfs:notification`-Event
// (composed), an dem ein Konsument den lokalisierten Toast aufhaengen kann
// (Toast-Text = server-i18n via pfs-content, separat: PFS-364). KEIN
// Domain-Text in der Bell.
//
// Attribute:
//   data-popover-id        targetId fuer pfs-popover-toggle event
//   data-initial-count     Badge-Count Initialwert (default: 0, server-rendered unread)
//   data-events-url        SSE-Quelle fuer liveBus (optional). Gesetzt + data-user-id = live-count.
//   data-event-name        SSE-Event-Name (default "pfs-notifications.delivery.pushed")
//   data-user-id           external_user_id fuer den Client-Filter (Pflicht fuer live-count)
//   data-event-id-field    Payload-Feld mit der external_user_id (default "external_user_id")

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { liveBus } from "../../store/live-bus.js";
import { subjectField } from "../../store/subject-field.js";
import { NOTIFICATION_PUSHED_EVENT } from "../../store/event-key.js";
import { apiFetch } from "../../store/api-fetch.js";
import { ICON_BUTTON_CSS } from "../../styles/icon-button.js";

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: inline-block; }
${ICON_BUTTON_CSS}
.badge {
  position: absolute;
  top: 0.125rem; right: 0.125rem;
  min-width: 1.125rem; height: 1.125rem; padding: 0 0.3rem;
  background: var(--pfs-color-danger); color: var(--pfs-color-on-primary);
  font-size: 0.625rem; font-weight: 700; line-height: 1;
  border-radius: 9999px;
  display: flex; align-items: center; justify-content: center;
  pointer-events: none;
}
.badge[hidden] { display: none; }
`;

defineComponent({
  tagName: "pfs-notification-bell",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;

    const popoverId = host.getAttribute("data-popover-id") ?? "";
    const initialCount = parseInt(host.getAttribute("data-initial-count") ?? "0", 10) || 0;
    // PFS-693: data-source-url = Unread-Count-Endpoint (`{count}`). Server-
    // seitig schon prefix-aufgeloest (Slot-Data via prefixSlotURLs), daher
    // as-is fetchen — Muster wie pfs-system-status.
    const sourceUrl = host.getAttribute("data-source-url")?.trim() || "";
    const eventsUrl = host.getAttribute("data-events-url")?.trim() || "";
    const eventName = host.getAttribute("data-event-name")?.trim() || NOTIFICATION_PUSHED_EVENT;
    const userId = host.getAttribute("data-user-id")?.trim() || "";
    const idField = host.getAttribute("data-event-id-field")?.trim() || "external_user_id";

    // count = Source-of-Truth fuer das Badge; server-rendered initial-count
    // + live-Increments + externe data-initial-count-Updates schreiben hier.
    let count = initialCount;

    const style = document.createElement("style");
    style.textContent = STYLES;

    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("part", "button"); // Host-Stempel-Fuge (analog theme-toggle)
    button.setAttribute("aria-label", "Benachrichtigungen");
    button.title = "Benachrichtigungen";
    button.innerHTML = `
      <svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/>
      </svg>
      <span class="badge" hidden></span>
    `;

    const badge = button.querySelector(".badge") as HTMLElement;
    const setCount = (n: number) => {
      if (n <= 0) {
        badge.hidden = true;
        badge.textContent = "";
      } else {
        badge.hidden = false;
        badge.textContent = n > 99 ? "99+" : String(n);
      }
    };
    setCount(initialCount);

    // PFS-693: EINE uniforme Regel — bei JEDEM Signal den autoritativen Count
    // NEU holen (kein kumuliertes +1/-1, keine Delta-Drift). refetch() ist die
    // einzige Wahrheit; Signale (initial · lokales pfs:notifications-changed ·
    // relevanter SSE-Push) rufen nur refetch. Best-effort — Fehler lassen den
    // Badge auf dem letzten Stand.
    const refetch = (): void => {
      if (!sourceUrl) return;
      void apiFetch<{ count: number }>(sourceUrl)
        .then((res) => {
          count = res?.count ?? 0;
          setCount(count);
        })
        .catch(() => {
          /* Badge bleibt auf dem letzten Wert */
        });
    };
    refetch();

    const onClick = () => {
      if (popoverId) {
        const rect = button.getBoundingClientRect();
        document.body.dispatchEvent(
          new CustomEvent("pfs:popover-toggle", {
            bubbles: false,
            detail: { targetId: popoverId, rect },
          }),
        );
      }
      host.dispatchEvent(
        new CustomEvent("pfs:notification-bell-click", {
          bubbles: true,
          composed: true,
        }),
      );
    };
    button.addEventListener("click", onClick);
    ctx.cleanup.push(() => button.removeEventListener("click", onClick));

    // External Count-Setter via attribute change. Konsumenten koennen
    // host.setAttribute("data-initial-count", "5") triggern + bell
    // updates. (MutationObserver weil attributeChangedCallback ohne
    // observedAttributes nicht laeuft.)
    const observer = new MutationObserver(() => {
      count = parseInt(host.getAttribute("data-initial-count") ?? "0", 10) || 0;
      setCount(count);
    });
    observer.observe(host, { attributes: true, attributeFilter: ["data-initial-count"] });
    ctx.cleanup.push(() => observer.disconnect());

    // PFS-693 lokaler Sync: eigene Aktionen im selben Browser (mark-read/
    // read-all im Inbox-WC) dispatchen `pfs:notifications-changed` an
    // document.body → Badge sofort neu ziehen (runter-live, ohne Realtime).
    if (sourceUrl) {
      const changedTarget = document.body;
      const onChanged = (): void => refetch();
      changedTarget.addEventListener("pfs:notifications-changed", onChanged);
      ctx.cleanup.push(() => changedTarget.removeEventListener("pfs:notifications-changed", onChanged));
    }

    // PFS-492/693 Realtime: liveBus-Subscribe wenn data-events-url + data-user-id
    // gesetzt. Filter auf die eigene external_user_id IM WC (liveBus bleibt
    // generisch) — nur um zu entscheiden, OB neu geladen wird, nicht um zu
    // rechnen. Pro relevantem Push: Count NEU holen + generisches `pfs:notification`-
    // Event (composed) fuer einen spaeteren Toast-Konsumenten. (Feuert erst mit
    // P3-Realtime = notification.created-Publisher → dann hoch-live automatisch.)
    if (eventsUrl && userId) {
      const onNotification = (value: unknown): void => {
        // ⚠️ subjectField liest ALLE drei Subject-Formen. Die fruehere Kette
        // hier kannte `subject`, aber nicht `Keys` — nach der Umstellung auf
        // die kanonische Form (PFS-1408) haette sie jedes Event still
        // verworfen.
        const ext = subjectField(value, idField);
        if (ext !== userId) return;
        refetch();
        host.dispatchEvent(
          new CustomEvent("pfs:notification", {
            bubbles: true,
            composed: true,
            detail: { value },
          }),
        );
      };
      ctx.cleanup.push(
        liveBus.track({
          url: eventsUrl,
          eventName,
          key: `pfs-notification-bell:${eventName}:${userId}`,
          onValue: onNotification,
        }),
      );
    }

    ctx.shadowRoot.appendChild(style);
    ctx.shadowRoot.appendChild(button);
  },
});
